import type {
  CSVValidationResult,
  AccountingConnection,
  AccountingAsset,
  ImportResult,
  FilterOptions,
  Asset,
  Transaction,
  TransactionFilterOptions,
  TransactionOverride,
} from '../types/asset-import.types';
import { TransactionType, TransactionSource } from '../types/asset-import.types';
import type { AgentSessionStatus } from '@/types/agent.types';

export interface FetchTransactionsWithAgentResult {
  transactions: Transaction[];
  totalCount: number;
  eligibleCount: number;
  alreadyUsedCount: number;
  excludedCount: number;
  agentSessionId?: string;
  agentStatus?: AgentSessionStatus;
}

interface BackendTransaction {
  primaryKey?: string;
  id?: string;
  transactionType?: string;
  transactionSource?: string;
  transactionDate?: string;
  reference?: string;
  description?: string;
  detail?: string;
  netValue?: number;
  grossValue?: number;
  vatAmount?: number;
  supplierName?: string;
  supplierCode?: string;
  analysisCode?: string;
  nominalAccount?: string;
  categoryCode?: string;
  costCentreCode?: string;
  projectCode?: string;
  quantity?: number;
  serialNumber?: string;
  status?: string;
  exclusionReason?: string;
  canCreateAsset?: boolean;
}
import { parseCSV } from '../utils/csv-parser';
import { validateAssetRow } from '../utils/asset-validator';
import { mockIntegrations } from '@/types/integrations';
import { authFetch, getAuthConfig } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';
import { getModuleMode } from '@/lib/feature-mode';

class AssetImportService {
  private baseUrl = '/api/assets/import';

  async uploadCSV(file: File): Promise<CSVValidationResult> {
    // Check the importAssets module mode from Feature Settings
    const importMode = getModuleMode('importAssets');
    
    if (importMode === 'demo') {
      // Demo mode: use frontend-only validation
      console.log('[AssetImportService] Demo mode - using frontend validation only');
      return this.validateCSVLocally(file);
    }
    
    // Live mode: use backend validation (includes Financials integration validation)
    console.log('[AssetImportService] Live mode - using backend validation');
    return this.uploadCSVToBackend(file);
  }

  // Backend validation - calls the API for full validation including Financials
  private async uploadCSVToBackend(file: File): Promise<CSVValidationResult> {
    const formData = new FormData();
    formData.append('file', file);

    const apiUrl = getApiUrl();
    
    // Step 1: Upload the CSV file to create an import job
    // Note: authFetch handles Authorization and X-Instance-Id headers automatically
    // Don't set Content-Type for FormData - browser will set it with boundary
    const uploadResponse = await authFetch(`${apiUrl}/api/import/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!uploadResponse.ok) {
      const errorData = await uploadResponse.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(errorData.message || 'Upload failed');
    }

    const uploadResult = await uploadResponse.json();
    const jobId = uploadResult.id;

    // Step 2: Validate the import (this runs backend validation including Financials)
    // Note: authFetch handles Authorization and X-Instance-Id headers automatically
    const validateResponse = await authFetch(`${apiUrl}/api/import/${jobId}/validate`, {
      method: 'POST',
    });

    if (!validateResponse.ok) {
      const errorData = await validateResponse.json().catch(() => ({ message: 'Validation failed' }));
      throw new Error(errorData.message || 'Validation failed');
    }

    const validationResult = await validateResponse.json();

    // Map backend validation result to frontend format
    const errors: import('../types/asset-import.types').CSVValidationError[] = (validationResult.errors || []).map((err: { rowNumber: number; field: string; message: string }) => ({
      row: err.rowNumber,
      field: err.field,
      error: err.message,
    }));

    // Map backend warnings to frontend format (Financials field mismatches)
    const warnings: import('../types/asset-import.types').CSVValidationWarning[] = (validationResult.warnings || []).map((warn: { rowNumber: number; field: string; message: string }) => ({
      row: warn.rowNumber,
      field: warn.field,
      message: warn.message,
    }));

    // Convert preview rows to Asset format for display
    const validAssets: Asset[] = (validationResult.previewRows || []).map((row: Record<string, string>, index: number) => ({
      id: `temp-${index}`,
      assetCode: row['AssetCode'] || row['Asset Code'] || '',
      description: row['AssetName'] || row['Asset Name'] || '',
      category: row['Category'] || '',
      boughtValue: parseFloat((row['AcquisitionCost'] || row['Acquisition Cost'] || '0').replace(/[£,]/g, '')),
      boughtDate: row['AcquisitionDate'] || row['Acquisition Date'] || '',
      supplier: row['SupplierName'] || row['Supplier Name'] || '',
      serialNumber: row['SerialNumber'] || row['Serial Number'] || '',
      location: row['Location'] || '',
      notes: row['Notes'] || '',
    }));

    return {
      valid: validationResult.isValid,
      totalRows: validationResult.validRows + validationResult.invalidRows,
      validRows: validationResult.validRows,
      errorCount: errors.length,
      warningCount: warnings.length,
      errors: errors,
      warnings: warnings,
      validAssets: validAssets,
      previewAssets: validAssets.slice(0, 5),
      jobId: jobId, // Store job ID for later import
    };
  }

  // Frontend validation - used for demo mode when backend is unavailable
  private async validateCSVLocally(file: File): Promise<CSVValidationResult> {
    // Parse CSV
    const parseResult = await parseCSV(file);
    
    if (parseResult.errors.length > 0) {
      return {
        valid: false,
        totalRows: 0,
        validRows: 0,
        errorCount: parseResult.errors.length,
        warningCount: 0,
        errors: parseResult.errors,
        warnings: [],
        validAssets: [],
        previewAssets: []
      };
    }

    // Validate each row
    const allErrors: import('../types/asset-import.types').CSVValidationError[] = [];
    const validAssets: Asset[] = [];

    parseResult.data.forEach((row, index) => {
      const rowNumber = index + 2; // +2 because row 1 is header, and we're 0-indexed
      const rowErrors = validateAssetRow(row);
      
      if (rowErrors.length > 0) {
        // Set the row number on each error
        rowErrors.forEach(error => {
          error.row = rowNumber;
        });
        allErrors.push(...rowErrors);
      } else {
        // Convert to Asset - support both old and new column names
        const assetCode = row['Asset Code'] || '';
        const assetName = row['Asset Name'] || row['Description'] || '';
        const category = row['Category'] || '';
        const acquisitionCost = row['Acquisition Cost'] || row['Bought Value'] || '0';
        const acquisitionDate = row['Acquisition Date'] || row['Bought Date'] || '';
        const supplierName = row['Supplier Name'] || row['Supplier'] || '';
        const serialNumber = row['Serial Number'] || '';
        const location = row['Location'] || '';
        const notes = row['Notes'] || '';

        validAssets.push({
          id: `temp-${index}`,
          assetCode: assetCode,
          description: assetName,
          category: category,
          boughtValue: parseFloat(acquisitionCost.replace(/[£,]/g, '')),
          boughtDate: acquisitionDate,
          supplier: supplierName,
          serialNumber: serialNumber,
          location: location,
          notes: notes
        });
      }
    });

    return {
      valid: allErrors.length === 0,
      totalRows: parseResult.data.length,
      validRows: validAssets.length,
      errorCount: allErrors.length,
      warningCount: 0,
      errors: allErrors,
      warnings: [],
      validAssets: validAssets,
      previewAssets: validAssets.slice(0, 5)
    };
  }

  // Process pre-parsed CSV data (from modal navigation)
  // Converts parsed data back to CSV and uploads to backend for validation
  async uploadCSVFromParsedData(parseResult: import('../types/asset-import.types').CSVParseResult, fileName: string): Promise<CSVValidationResult> {
    try {
      if (parseResult.errors.length > 0) {
        return {
          valid: false,
          totalRows: 0,
          validRows: 0,
          errorCount: parseResult.errors.length,
          warningCount: 0,
          errors: parseResult.errors,
          warnings: [],
          validAssets: [],
          previewAssets: []
        };
      }

      // Convert parsed data back to CSV format for backend upload
      if (parseResult.data.length === 0) {
        return {
          valid: false,
          totalRows: 0,
          validRows: 0,
          errorCount: 1,
          warningCount: 0,
          errors: [{ row: 0, field: '', error: 'No data rows found in CSV' }],
          warnings: [],
          validAssets: [],
          previewAssets: []
        };
      }

      // Get headers from first row's keys
      const headers = Object.keys(parseResult.data[0]);
      const csvLines = [headers.join(',')];
      
      // Add data rows
      parseResult.data.forEach(row => {
        const values = headers.map(header => {
          const value = row[header] || '';
          // Escape values that contain commas or quotes
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        });
        csvLines.push(values.join(','));
      });

      const csvContent = csvLines.join('\n');
      const csvBlob = new Blob([csvContent], { type: 'text/csv' });
      const csvFile = new File([csvBlob], fileName || 'import.csv', { type: 'text/csv' });

      // Use the main uploadCSV method which now calls the backend
      return this.uploadCSV(csvFile);
    } catch (error) {
      throw new Error(`Processing failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async downloadCSVTemplate(): Promise<Blob> {
    // For now, return a mock blob
    // In production, this would call the API
    const { generateCSVTemplate } = await import('../utils/csv-parser');
    const csv = generateCSVTemplate();
    return new Blob([csv], { type: 'text/csv' });
  }

  async importValidAssets(assetIds: string[], jobId?: string): Promise<ImportResult> {
    // Check the importAssets module mode from Feature Settings
    const importMode = getModuleMode('importAssets');
    
    if (importMode === 'live' && jobId) {
      // Live mode: call backend to start the import
      console.log('[AssetImportService] Live mode - starting backend import for job:', jobId);
      try {
        const apiUrl = getApiUrl();
        
        // Note: authFetch handles Authorization and X-Instance-Id headers automatically
        const response = await authFetch(`${apiUrl}/api/import/${jobId}/start`, {
          method: 'POST',
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Import failed' }));
          throw new Error(errorData.message || 'Import failed');
        }
        
        // The backend processes asynchronously, return success
        // The import progress will be tracked via the ImportStatusIndicator
        return {
          success: true,
          importedCount: assetIds.length,
          failedCount: 0
        };
      } catch (error) {
        console.error('Backend import failed:', error);
        return {
          success: false,
          importedCount: 0,
          failedCount: assetIds.length,
          message: error instanceof Error ? error.message : 'Import failed'
        };
      }
    }
    
    // Demo mode: mock implementation
    console.log('[AssetImportService] Demo mode - using mock import');
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          importedCount: assetIds.length,
          failedCount: 0
        });
      }, 1500);
    });
  }

  async exportValidationErrors(errors: any[]): Promise<Blob> {
    // Create CSV from errors
    const headers = ['Row', 'Field', 'Error', 'Value'];
    let csv = headers.join(',') + '\n';
    
    errors.forEach(error => {
      const row = [
        error.row,
        error.field,
        `"${error.error}"`,
        error.value ?? ''
      ];
      csv += row.join(',') + '\n';
    });

    return new Blob([csv], { type: 'text/csv' });
  }

  async getAccountingConnection(): Promise<AccountingConnection> {
    // Mock implementation
    // In production, this would call: GET /api/assets/import/accounting/connection
    return new Promise((resolve) => {
      setTimeout(() => {
        const systemType = 'access-financials';
        const integration = mockIntegrations.find(i => i.id === systemType);
        
        resolve({
          id: '1',
          systemName: integration?.name ?? 'Access Financials',
          systemType: systemType,
          isConnected: true,
          lastSyncDate: new Date().toISOString(),
          logoUrl: integration?.logo
        });
      }, 500);
    });
  }

  async fetchAccountingAssets(filters: FilterOptions): Promise<AccountingAsset[]> {
    // Mock implementation
    // In production, this would call: POST /api/assets/import/accounting/fetch
    return new Promise((resolve) => {
      setTimeout(() => {
        // Realistic asset descriptions and details
        const assetTemplates = [
          // Computer Equipment
          { name: 'Dell Latitude 5420 Laptop', category: 'Computer Equipment', manufacturer: 'Dell', model: 'Latitude 5420', priceRange: [800, 1500] },
          { name: 'HP EliteBook 840 G8', category: 'Computer Equipment', manufacturer: 'HP', model: 'EliteBook 840 G8', priceRange: [900, 1600] },
          { name: 'MacBook Pro 14" M2', category: 'Computer Equipment', manufacturer: 'Apple', model: 'MacBook Pro 14"', priceRange: [1800, 2500] },
          { name: 'Dell UltraSharp 27" Monitor', category: 'Computer Equipment', manufacturer: 'Dell', model: 'U2722DE', priceRange: [400, 600] },
          { name: 'HP LaserJet Pro MFP M428fdw', category: 'Computer Equipment', manufacturer: 'HP', model: 'M428fdw', priceRange: [500, 800] },
          { name: 'Lenovo ThinkCentre Desktop', category: 'Computer Equipment', manufacturer: 'Lenovo', model: 'ThinkCentre M720', priceRange: [700, 1200] },
          { name: 'Microsoft Surface Pro 9', category: 'Computer Equipment', manufacturer: 'Microsoft', model: 'Surface Pro 9', priceRange: [1000, 1800] },
          { name: 'Samsung 32" Curved Monitor', category: 'Computer Equipment', manufacturer: 'Samsung', model: 'C32F391', priceRange: [300, 500] },
          
          // Furniture & Fixtures
          { name: 'Herman Miller Aeron Chair', category: 'Furniture & Fixtures', manufacturer: 'Herman Miller', model: 'Aeron', priceRange: [1200, 1800] },
          { name: 'Steelcase Leap V2 Chair', category: 'Furniture & Fixtures', manufacturer: 'Steelcase', model: 'Leap V2', priceRange: [900, 1400] },
          { name: 'Standing Desk Electric Adjustable', category: 'Furniture & Fixtures', manufacturer: 'FlexiSpot', model: 'E7', priceRange: [500, 800] },
          { name: 'Executive Office Desk Oak', category: 'Furniture & Fixtures', manufacturer: 'Office Furniture Ltd', model: 'Executive Oak', priceRange: [800, 1500] },
          { name: 'Meeting Room Table 8-Seater', category: 'Furniture & Fixtures', manufacturer: 'Office Supplies Ltd', model: 'Conference 8', priceRange: [1200, 2000] },
          { name: 'Filing Cabinet 4-Drawer Steel', category: 'Furniture & Fixtures', manufacturer: 'Bisley', model: 'BS4E', priceRange: [300, 500] },
          { name: 'Reception Desk L-Shaped', category: 'Furniture & Fixtures', manufacturer: 'Reception Furniture Co', model: 'L-Shape Pro', priceRange: [2000, 3500] },
          
          // Vehicles
          { name: 'Ford Transit Custom Van', category: 'Vehicles', manufacturer: 'Ford', model: 'Transit Custom 320', priceRange: [25000, 35000] },
          { name: 'Volkswagen Transporter T6.1', category: 'Vehicles', manufacturer: 'Volkswagen', model: 'Transporter T6.1', priceRange: [28000, 38000] },
          { name: 'Mercedes-Benz Sprinter', category: 'Vehicles', manufacturer: 'Mercedes-Benz', model: 'Sprinter 314 CDI', priceRange: [32000, 42000] },
          { name: 'Toyota Hilux Double Cab', category: 'Vehicles', manufacturer: 'Toyota', model: 'Hilux Invincible', priceRange: [30000, 40000] },
          { name: 'Nissan Leaf Electric', category: 'Vehicles', manufacturer: 'Nissan', model: 'Leaf e+ Tekna', priceRange: [28000, 35000] },
          
          // Machinery
          { name: 'Forklift Truck 2.5 Tonne', category: 'Machinery', manufacturer: 'Toyota Material Handling', model: '8FBN25', priceRange: [18000, 25000] },
          { name: 'Pallet Jack Electric', category: 'Machinery', manufacturer: 'Crown', model: 'PE4500', priceRange: [8000, 12000] },
          { name: 'Industrial Air Compressor', category: 'Machinery', manufacturer: 'Atlas Copco', model: 'GA 15', priceRange: [12000, 18000] },
          { name: 'CNC Milling Machine', category: 'Machinery', manufacturer: 'Haas', model: 'VF-2SS', priceRange: [45000, 65000] },
          { name: 'Packaging Machine Automatic', category: 'Machinery', manufacturer: 'Bosch', model: 'SVE 2520 D', priceRange: [35000, 50000] },
          
          // Office Equipment
          { name: 'Cisco IP Phone 8841', category: 'Office Equipment', manufacturer: 'Cisco', model: '8841', priceRange: [200, 350] },
          { name: 'Polycom Conference Phone', category: 'Office Equipment', manufacturer: 'Polycom', model: 'SoundStation IP 6000', priceRange: [400, 600] },
          { name: 'Shredder Cross-Cut Heavy Duty', category: 'Office Equipment', manufacturer: 'Fellowes', model: 'Powershred 99Ci', priceRange: [300, 500] },
          { name: 'Laminator A3 Professional', category: 'Office Equipment', manufacturer: 'GBC', model: 'Fusion 3000L', priceRange: [250, 400] },
          { name: 'Projector Full HD 4000 Lumens', category: 'Office Equipment', manufacturer: 'Epson', model: 'EB-2250U', priceRange: [1500, 2500] },
        ];

        const suppliers = [
          'Dell Technologies UK Ltd',
          'HP UK Ltd',
          'Apple UK',
          'Office Supplies Ltd',
          'Ford Dealer UK',
          'Volkswagen Commercial Vehicles',
          'Mercedes-Benz UK',
          'Toyota GB',
          'Nissan Motor GB',
          'Office Furniture Ltd',
          'Herman Miller UK',
          'Steelcase UK',
          'Machinery Direct Ltd',
          'Atlas Copco UK',
          'Bosch UK',
          'Cisco Systems UK',
          'Epson UK',
          'Tech Supplies Ltd',
          'Equipment Solutions UK',
          'Industrial Equipment Co'
        ];

        const locations = [
          'Head Office - London',
          'Distribution Centre - Birmingham',
          'Regional Office - Manchester',
          'Warehouse - Leeds',
          'Branch Office - Bristol',
          'Manufacturing Plant - Sheffield',
          'Sales Office - Edinburgh',
          'Service Centre - Cardiff'
        ];

        // Generate 47 realistic assets
        const mockAssets: AccountingAsset[] = [];
        
        // Add some already imported assets (about 10%)
        const alreadyImportedCount = 5;
        
        for (let i = 0; i < 47; i++) {
          const template = assetTemplates[i % assetTemplates.length];
          const isAlreadyImported = i < alreadyImportedCount;
          
          // Generate realistic purchase date (within last 3 years)
          const daysAgo = Math.floor(Math.random() * 1095); // 3 years
          const purchaseDate = new Date();
          purchaseDate.setDate(purchaseDate.getDate() - daysAgo);
          
          // Generate price within template range
          const [minPrice, maxPrice] = template.priceRange;
          const price = Math.floor(Math.random() * (maxPrice - minPrice) + minPrice);
          
          // Generate serial number (80% have serial numbers)
          const hasSerial = Math.random() > 0.2;
          const serialNumber = hasSerial 
            ? `${template.manufacturer.substring(0, 3).toUpperCase()}${String(i + 1000).padStart(6, '0')}` 
            : undefined;
          
          // Select random supplier (prefer manufacturer if available)
          const supplier = Math.random() > 0.3 
            ? suppliers.find(s => s.includes(template.manufacturer)) || suppliers[i % suppliers.length]
            : suppliers[i % suppliers.length];
          
          // Select random location
          const location = locations[i % locations.length];
          
          // Generate invoice reference
          const invoiceRef = `INV-${new Date(purchaseDate).getFullYear()}-${String(i + 1).padStart(4, '0')}`;
          
          // Generate PO number
          const poNumber = `PO-${String(i + 10000).padStart(6, '0')}`;

          mockAssets.push({
            id: `${i + 1}`,
            assetCode: `FA${String(i + 1).padStart(4, '0')}`,
            description: template.name,
            category: template.category,
            purchasePrice: price,
            purchaseDate: purchaseDate.toISOString().split('T')[0],
            supplier: supplier,
            serialNumber: serialNumber,
            manufacturer: template.manufacturer,
            model: template.model,
            location: location,
            invoiceReference: invoiceRef,
            purchaseOrderNumber: poNumber,
            status: isAlreadyImported ? 'already-imported' : 'new',
            notes: isAlreadyImported ? 'This asset has already been imported' : undefined
          });
        }

        resolve(mockAssets);
      }, 2000);
    });
  }

  async importAccountingAssets(assetIds: string[]): Promise<ImportResult> {
    // Mock implementation
    // In production, this would call: POST /api/assets/import/accounting/import
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          importedCount: assetIds.length,
          failedCount: 0
        });
      }, 1500);
    });
  }

  async fetchTransactions(
    filters: TransactionFilterOptions,
    enableAgentProcessing: boolean = false
  ): Promise<FetchTransactionsWithAgentResult> {
    const authConfig = getAuthConfig();
    const financialsInstanceId = authConfig.financialsInstanceId ?? authConfig.instanceId ?? '';

    if (!financialsInstanceId) {
      console.warn('[AssetImportService] No financialsInstanceId configured, returning empty transactions');
      return {
        transactions: [],
        totalCount: 0,
        eligibleCount: 0,
        alreadyUsedCount: 0,
        excludedCount: 0,
      };
    }

    try {
      const apiUrl = getApiUrl();
      const params = new URLSearchParams();
      params.append('financialsInstanceId', financialsInstanceId);
      
      if (filters.dateFrom) {
        params.append('dateFrom', filters.dateFrom.toISOString());
      }
      if (filters.dateTo) {
        params.append('dateTo', filters.dateTo.toISOString());
      }
      if (filters.category) {
        params.append('categoryCode', filters.category);
      }
      if (filters.supplier) {
        params.append('supplierCode', filters.supplier);
      }
      if (filters.minValue !== undefined) {
        params.append('minValue', filters.minValue.toString());
      }
      if (filters.maxValue !== undefined) {
        params.append('maxValue', filters.maxValue.toString());
      }
      if (enableAgentProcessing) {
        params.append('enableAgentProcessing', 'true');
      }

      const response = await authFetch(
        `${apiUrl}/api/assets/financials/transactions/fetch?${params.toString()}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message ?? `Failed to fetch transactions: ${response.status}`);
      }

      const data = await response.json();
      
      const transactions = (data.transactions ?? []).map((t: BackendTransaction) => this.mapBackendTransactionToFrontend(t));
      
      return {
        transactions,
        totalCount: data.totalCount ?? transactions.length,
        eligibleCount: data.eligibleCount ?? 0,
        alreadyUsedCount: data.alreadyUsedCount ?? 0,
        excludedCount: data.excludedCount ?? 0,
        agentSessionId: data.agentSessionId,
        agentStatus: data.agentStatus,
      };
    } catch (error) {
      console.error('[AssetImportService] Error fetching transactions:', error);
      throw error;
    }
  }

  async createAssetsFromTransactions(
    transactionIds: string[], 
    useGrossValue: boolean = false,
    transactionOverrides?: Record<string, TransactionOverride>
  ): Promise<ImportResult> {
    const authConfig = getAuthConfig();
    const financialsInstanceId = authConfig.financialsInstanceId ?? authConfig.instanceId ?? '';

    if (!financialsInstanceId) {
      return {
        success: false,
        importedCount: 0,
        failedCount: transactionIds.length,
        message: 'No financialsInstanceId configured'
      };
    }

    try {
      const apiUrl = getApiUrl();
      const params = new URLSearchParams();
      params.append('financialsInstanceId', financialsInstanceId);

      const response = await authFetch(
        `${apiUrl}/api/assets/financials/transactions/import?${params.toString()}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            transactionIds,
            useGrossValue,
            transactionOverrides,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create assets: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        success: data.success,
        importedCount: data.assetsCreated ?? 0,
        failedCount: data.errors?.length ?? 0,
        skippedCount: data.assetsUpdated ?? 0,
        message: data.success 
          ? `Successfully created ${data.assetsCreated} assets from transactions`
          : 'Some transactions failed to import',
        errors: data.errors?.map((e: any) => e.errorMessage) || []
      };
    } catch (error) {
      console.error('[AssetImportService] Error creating assets from transactions:', error);
      return {
        success: false,
        importedCount: 0,
        failedCount: transactionIds.length,
        message: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  private mapBackendTransactionToFrontend(t: BackendTransaction): Transaction {
    const sourceMap: Record<string, TransactionSource> = {
      'PurchaseLedger': TransactionSource.PurchaseLedger,
      'NominalLedger': TransactionSource.NominalLedger,
      'Costing': TransactionSource.Costing,
    };

    const typeMap: Record<string, TransactionType> = {
      'Invoice': TransactionType.PurchaseInvoice,
      'CreditNote': TransactionType.PurchaseCreditNote,
      'Debit': TransactionType.NominalDebit,
      'Credit': TransactionType.NominalCredit,
      'Timesheet': TransactionType.CostingTimesheet,
      'Miscellaneous': TransactionType.CostingMiscellaneous,
    };

    const statusMap: Record<string, 'eligible' | 'already-used' | 'excluded'> = {
      'Eligible': 'eligible',
      'AlreadyUsed': 'already-used',
      'Excluded': 'excluded',
    };

    return {
      id: t.primaryKey ?? t.id ?? '',
      transactionType: typeMap[t.transactionType ?? ''] ?? TransactionType.PurchaseInvoice,
      transactionSource: sourceMap[t.transactionSource ?? ''] ?? TransactionSource.PurchaseLedger,
      transactionDate: t.transactionDate ? new Date(t.transactionDate).toISOString().split('T')[0] : '',
      transactionReference: t.reference ?? '',
      description: t.description ?? t.detail ?? '',
      netValue: t.netValue ?? 0,
      grossValue: t.grossValue ?? 0,
      vatAmount: t.vatAmount ?? 0,
      supplier: t.supplierName ?? t.supplierCode,
      analysisCode: t.analysisCode,
      nominalAccount: t.nominalAccount,
      category: t.categoryCode,
      costCentre: t.costCentreCode,
      project: t.projectCode,
      quantity: t.quantity,
      serialNumber: t.serialNumber,
      status: statusMap[t.status ?? ''] ?? 'eligible',
      exclusionReason: t.exclusionReason,
      canCreateAsset: t.canCreateAsset ?? true,
    };
  }
}

export const assetImportService = new AssetImportService();
